import { FC, useMemo, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import { Box, Button, Flex, Text } from "@radix-ui/themes";
import { Workflow, TriggerWorkflowRequest, WorkflowTriggerType } from "@/types/breeze/Workflow";
import CodeField from "@/components/form/CodeField";
import SearchableSelect, { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { useMutation, useQuery } from "@tanstack/react-query";
import { workflowService } from "@/services/breeze/WorkflowService";
import { entityService } from "@/services/breeze/EntityService";
import { anyService } from "@/services/breeze/AnyService";
import Modal from "@/components/ui/Modal";

interface TriggerWorkflowDialogProps {
  workflow: Workflow | null;
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

const DISPLAY_FIELD_CANDIDATES = ["name", "title", "display_name", "label", "email", "username"];
const PICKER_FIELDS = ["id", ...DISPLAY_FIELD_CANDIDATES];

function recordLabel(row: Record<string, unknown>): string {
  for (const key of DISPLAY_FIELD_CANDIDATES) {
    const v = row[key];
    if (typeof v === "string" && v.trim()) return `${v} (#${row.id})`;
  }
  return `#${row.id}`;
}

interface FormValues {
  entity_name: string;
  entity_id: number | null;
  data: string;
}

const TriggerWorkflowDialog: FC<TriggerWorkflowDialogProps> = ({
  workflow,
  isOpen,
  onSuccess,
  onClose,
}) => {
  const triggerWorkflow = useMutation({
    mutationFn: (data: TriggerWorkflowRequest) =>
      workflowService.triggerWorkflow(workflow!.id, data),
    onSuccess: () => onSuccess(),
  });

  const defaultEntityName = useMemo(() => {
    if (!workflow) return "";
    const manual = workflow.triggers?.find((t) => (t.config?.manual_entities?.length ?? 0) > 0);
    const evt = workflow.triggers?.find((t) => t.config?.event_entity);
    return manual?.config?.manual_entities?.[0] || evt?.config?.event_entity || "";
  }, [workflow]);

  const isTimedOnly = useMemo(() => {
    const triggers = workflow?.triggers ?? [];
    if (triggers.length === 0) return false;
    return triggers.every((t) => t.type === WorkflowTriggerType.Timed);
  }, [workflow]);

  if (!workflow) return null;

  return (
    <Formik<FormValues>
      initialValues={{ entity_name: defaultEntityName, entity_id: null, data: "{}" }}
      onSubmit={(values) => {
        triggerWorkflow.mutate({
          entity_name: values.entity_name || undefined,
          entity_id: values.entity_id ?? undefined,
          data: values.data,
        });
      }}
      enableReinitialize
    >
      <Form id="trigger-workflow-form">
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="lg"
          title={`Test Workflow "${workflow.name}"`}
          description={
            isTimedOnly
              ? "Timed workflow — fire it once now, ignoring the schedule."
              : "Pick the record to fire the workflow against — or edit the raw payload below."
          }
          actions={
            <>
              <Button variant="outline" color="gray" size="2" onClick={onClose} disabled={triggerWorkflow.isPending}>
                Cancel
              </Button>
              <Button
                variant="solid"
                size="2"
                type="submit"
                form="trigger-workflow-form"
                loading={triggerWorkflow.isPending}
              >
                Run Test
              </Button>
            </>
          }
        >
          <PickerBody isOpen={isOpen} isTimedOnly={isTimedOnly} />
        </Modal>
      </Form>
    </Formik>
  );
};

function PickerBody({ isOpen, isTimedOnly }: { isOpen: boolean; isTimedOnly: boolean }) {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const [recordId, setRecordId] = useState<string>("");

  const entitiesQuery = useQuery({
    queryKey: ["entities-list"],
    queryFn: () => entityService.getAll(),
    enabled: isOpen && !isTimedOnly,
  });
  const entityOptions: SearchableSelectOption[] = useMemo(() =>
    (entitiesQuery.data ?? []).map((e) => ({ value: e.name, label: e.name })),
  [entitiesQuery.data]);

  const recordsQuery = useQuery({
    queryKey: ["trigger-dialog-records", values.entity_name],
    queryFn: () => anyService.getAll<Record<string, unknown>>(values.entity_name, PICKER_FIELDS, "", 1, 100),
    enabled: isOpen && !!values.entity_name,
  });
  const recordOptions: SearchableSelectOption[] = useMemo(() => {
    const rows = recordsQuery.data?.items ?? [];
    return rows.map((r) => ({ value: String(r.id), label: recordLabel(r) }));
  }, [recordsQuery.data]);

  const applyPickers = async (nextEntity: string, nextRecordId: string) => {
    setFieldValue("entity_name", nextEntity);
    setRecordId(nextRecordId);
    const parsedId = nextRecordId ? Number(nextRecordId) : null;
    setFieldValue("entity_id", parsedId);
    let data: Record<string, unknown> = {};
    if (nextEntity && parsedId) {
      try {
        data = await anyService.getById<Record<string, unknown>>(nextEntity, parsedId) ?? {};
      } catch (err) {
        console.warn("Trigger dialog: couldn't fetch record for payload seed", err);
      }
    }
    setFieldValue("data", JSON.stringify(data, null, 2));
  };

  return (
    <Flex p="4" direction="column" gap="4">
      {!isTimedOnly && (
        <Flex gap="3" wrap="wrap">
          <Box style={{ flex: "1 1 220px" }}>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>Entity</Text>
            <SearchableSelect
              value={values.entity_name}
              onChange={(v) => applyPickers(v, "")}
              options={entityOptions}
              placeholder={entitiesQuery.isLoading ? "Loading…" : "Pick an entity"}
              searchPlaceholder="Search entities…"
            />
          </Box>
          <Box style={{ flex: "1 1 260px" }}>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>Record</Text>
            <SearchableSelect
              value={recordId}
              onChange={(v) => applyPickers(values.entity_name, v)}
              options={recordOptions}
              placeholder={
                !values.entity_name
                  ? "Pick an entity first"
                  : recordsQuery.isLoading
                    ? "Loading records…"
                    : "Pick a record"
              }
              searchPlaceholder="Search records…"
              disabled={!values.entity_name || recordsQuery.isLoading}
            />
          </Box>
        </Flex>
      )}

      <Box>
        <Text as="label" size="1" color="gray" style={{ display: "block", marginBottom: 4 }}>
          Payload (advanced — edit to override, e.g. seed <code>data</code> with extra fields)
        </Text>
        <CodeField name="data" label="" language="json" />
      </Box>
    </Flex>
  );
}

export default TriggerWorkflowDialog;
